import {
  IChatServiceClient,
  IChatServiceAmbassador,
  Conversation,
  LiveChat,
  ToolExecutionRegisry,
  StartChatStrategy,
  StartChatResult,
  DomainReferences,
} from './types';
import { v4 as uuid } from 'uuid';
import { createChatMessagingManager } from './ChatServiceClient/ChatMessagingManager';

const safeError = (error: unknown) =>
  error instanceof Error ? error : new Error(JSON.stringify(error));

export class ChatServiceClient implements IChatServiceClient {
  constructor(private readonly ambassador: IChatServiceAmbassador) {}

  async startChat(
    strategy: StartChatStrategy,
    domains: DomainReferences,
    toolsExecutorRegistry: ToolExecutionRegisry,
    options?: Partial<{ enableLogging: boolean; noValidate: boolean }>,
  ): Promise<StartChatResult> {
    let _conversation: Conversation;
    try {
      _conversation = await this.initConversation(strategy);
    } catch (error: unknown) {
      return {
        ok: false,
        cause: 'conversation-init-error',
        error: safeError(error),
      };
    }
    const chatManager = createChatMessagingManager({
      conversationId: _conversation.id,
      domains,
      toolsExecutorRegistry,
      sendMessage: this.ambassador.sendMessage,
      initialMessages: _conversation.messages.map(({ id: _, ...rest }) => ({
        ...rest,
      })),
      options: {
        enableLogging: false,
        noValidate: false,
        ...options,
      },
    });
    const chat: LiveChat = {
      id: String(_conversation.id),
      messages: chatManager.messages,
      sendUserMessage: (msg, agentName) =>
        chatManager.sendUserMessage(msg, agentName),
      loadMessages: (conversationId) =>
        this.ambassador
          .getConversationMessages({ conversationId })
          .then((res) => res.messages ?? []),
      getConversationStatus: (conversationId) =>
        this.ambassador
          .getConversationStatus({ conversationId })
          .then((res) => res.statusMessage ?? ''),
    };
    return { ok: true, chat };
  }

  private async initConversation(strategy: StartChatStrategy) {
    const conversation: Conversation = {
      id: uuid(),
      messages: [],
    };
    if (strategy.type === 'existing-conversation') {
      const { messages } = await this.ambassador.getConversationMessages({
        conversationId: strategy.id,
      });
      conversation.id = `${strategy.id}`;
      conversation.messages = [...(messages ?? [])];
    } else if (strategy.type === 'new-conversation') {
      const { conversationId } = await this.ambassador.createConversation({
        origin: strategy.origin ?? null,
      });
      if (!conversationId) {
        throw new Error('conversationId is missing');
      }
      conversation.id = `${conversationId}`;
      const { messages } = await this.ambassador.getConversationMessages({
        conversationId: conversation.id,
      });
      conversation.messages = [...(messages ?? [])];
    } else {
      throw new Error(`unknown strategy ${(strategy as any).type}`);
    }

    return conversation;
  }
}
